import React, { useState } from 'react';
import { T, font, mono, trackUrl } from '../utils/theme';
import * as api from '../utils/api';
import { Card, Sect, Btn, Inp, Sel, SearchBar, SBadge, IcCheck, IcTrash, IcEdit, IcMsg, useIsMobile, matchesSearch } from './UI';
import { money } from '../utils/money';

export default function SupplierShipments({ lang, supplier, products, shipments, shipmentComments, reload, Lk }) {
  const isMobile = useIsMobile();
  const [shipSel, setShipSel] = useState({});
  const [shipQtys, setShipQtys] = useState({});
  const [shipTrack, setShipTrack] = useState('');
  const [shipCourier, setShipCourier] = useState('');
  const [shipCost, setShipCost] = useState('');
  const [editShipId, setEditShipId] = useState(null);
  const [editItemQtys, setEditItemQtys] = useState({});
  const [editShipCost, setEditShipCost] = useState({});
  const [qtyFlash, setQtyFlash] = useState({});
  const [search, setSearch] = useState('');
  const [openComment, setOpenComment] = useState(null);
  const [commentText, setCommentText] = useState('');

  const preparingP = products.filter(p => p.status === 'ordering' && p.assigned_supplier === supplier.id && !p.shipment_status);
  const myShips = shipments.filter(s => s.supplier_id === supplier.id && s.status === 'shipped');

  const filteredShips = myShips.filter(s => {
    if (!search) return true;
    const prods = (s.items || []).map(i => products.find(p => p.id === i.product_id)).filter(Boolean);
    return matchesSearch(search, s.tracking || '') || prods.some(p => matchesSearch(search, p.name) || matchesSearch(search, p.sku || ''));
  });

  const doEditItemQty = async (shipId, productId, newQty) => {
    await api.editShipmentItemQty(shipId, productId, parseInt(newQty) || 0);
    await reload();
  };

  const doEditShipCost = async (shipId, cost) => {
    await api.updateShipment(shipId, { shipping_cost: parseFloat(cost) || 0 });
    await reload();
  };

  const createShipment = async () => {
    const sel = Object.entries(shipSel).filter(([_, v]) => v).map(([k]) => k);
    if (sel.length === 0) return;
    const missing = sel.filter(pid => !shipQtys[pid] || parseInt(shipQtys[pid]) <= 0);
    if (missing.length > 0) {
      const flashObj = {};
      missing.forEach(pid => { flashObj[pid] = true; });
      setQtyFlash(flashObj);
      setTimeout(() => setQtyFlash({}), 300);
      setTimeout(() => { setQtyFlash(flashObj); setTimeout(() => setQtyFlash({}), 300); }, 400);
      setTimeout(() => { setQtyFlash(flashObj); setTimeout(() => setQtyFlash({}), 300); }, 800);
      return;
    }
    const items = sel.map(pid => ({ product_id: pid, qty: parseInt(shipQtys[pid]) }));
    await api.createShipment({ supplier_id: supplier.id, tracking: shipTrack.trim() || '—', courier: shipCourier || '—', items, shipping_cost: parseFloat(shipCost) || 0 });
    setShipSel({}); setShipQtys({}); setShipTrack(''); setShipCourier(''); setShipCost('');
    await reload();
  };

  const doDeleteShipment = async (shipId) => {
    await api.deleteShipment(shipId);
    await reload();
  };

  const doRemoveItem = async (shipId, productId) => {
    await api.removeShipmentItem(shipId, productId);
    await reload();
  };

  const doSendComment = async (shipId) => {
    if (!commentText.trim()) return;
    await api.addShipmentComment({ shipment_id: shipId, author: supplier.name, author_type: 'supplier', text: commentText.trim() });
    setCommentText('');
    await reload();
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 22px', fontSize: 22, fontWeight: 900, color: T.text }}>{Lk('shipments')}</h2>

      {/* Preparing items */}
      {preparingP.length > 0 && (
        <Sect title={lang === 'zh' ? '准备发货' : 'Ready to Ship'}>
          {/* Sticky shipping form */}
          <div style={{ position: 'sticky', top: 0, zIndex: 10, background: T.bg, paddingBottom: 12, marginBottom: 10, borderBottom: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 12, color: T.orange, marginBottom: 10 }}>
              {lang === 'zh' ? '勾选本次一起发货的产品，填写快递信息，然后点击发货。' : 'Tick the items going out together, fill in the tracking details, then click Ship.'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px auto', gap: 10, alignItems: 'end' }}>
              <Inp label={Lk('tracking')} placeholder="e.g. TRK-123456789" value={shipTrack} onChange={e => setShipTrack(e.target.value)} />
              <Sel label={Lk('courier')} value={shipCourier} onChange={e => setShipCourier(e.target.value)}>
                <option value="">{lang === 'zh' ? '选择快递' : 'Select courier'}</option>
                <option value="DHL">DHL</option><option value="UPS">UPS</option><option value="FedEx">FedEx</option>
              </Sel>
              <Inp label={lang === 'zh' ? '运费' : 'Ship Cost'} placeholder="0.00" type="text" inputMode="decimal" value={shipCost} onChange={e => setShipCost(e.target.value)} />
              <Btn onClick={createShipment}>📦 {lang === 'zh' ? `发货 (${Object.values(shipSel).filter(Boolean).length})` : `Ship (${Object.values(shipSel).filter(Boolean).length})`}</Btn>
            </div>
          </div>
          {/* Item list */}
          {preparingP.map(p => (
            <div key={p.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', marginBottom: 4,
              background: shipSel[p.id] ? T.accent + '12' : T.surface,
              border: `1px solid ${shipSel[p.id] ? T.accent + '50' : T.border}`, borderRadius: 8,
            }}>
              <div onClick={() => setShipSel(prev => ({ ...prev, [p.id]: !prev[p.id] }))} style={{
                width: 20, height: 20, borderRadius: 4, border: `2px solid ${shipSel[p.id] ? T.accent : T.border}`,
                background: shipSel[p.id] ? T.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
              }}>
                {shipSel[p.id] && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>}
              </div>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 700, color: T.text, fontSize: 13 }}>{p.name}</span>
                <span style={{ fontFamily: mono, fontSize: 11, color: T.dim, marginLeft: 8 }}>{p.sku}</span>
              </div>
              <span style={{ fontFamily: mono, fontSize: 12, color: T.muted, marginRight: 4 }}>{money((p.quotes?.[supplier.id]?.price || p.cost_price || 0))}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: T.muted }}>{Lk('shipQty')}:</span>
                <input type="number" value={shipQtys[p.id] || ''} onChange={e => setShipQtys({ ...shipQtys, [p.id]: e.target.value })}
                  placeholder={String(p.assigned_qty || p.qty_ordered || 0)}
                  style={{ fontFamily: mono, fontSize: 12, padding: '5px 8px', width: 60, background: T.bg, border: `1px solid ${qtyFlash[p.id] ? T.danger : T.border}`, borderRadius: 4, color: T.text, outline: 'none', boxShadow: qtyFlash[p.id] ? `0 0 6px ${T.danger}60` : 'none', transition: 'all 0.15s' }} />
              </div>
              <SBadge label={Lk('preparing')} color={T.purple} bg={T.purpleBg} />
            </div>
          ))}
        </Sect>
      )}

      {/* Shipment history */}
      <Sect title={lang === 'zh' ? '发货记录' : 'Shipment History'}>
        <SearchBar sticky value={search} onChange={setSearch} placeholder={Lk('searchProduct')} style={{ marginBottom: 12 }} />

        {filteredShips.length === 0 ? (
          <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic' }}>{lang === 'zh' ? '暂无发货记录。' : 'No shipments yet.'}</div>
        ) : filteredShips.map(s => {
          const isEditing = editShipId === s.id;
          const items = (s.items || []).map(i => {
            const p = products.find(x => x.id === i.product_id);
            return { ...i, product: p };
          }).filter(i => i.product);
          const tUrl = trackUrl(s.courier, s.tracking);
          const comments = (shipmentComments || []).filter(c => c.shipment_id === s.id);
          const hasComments = comments.length > 0;
          const isCommentOpen = openComment === s.id;

          return (
            <Card key={s.id} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, color: T.dim, fontFamily: mono }}>{s.created_at}</span>
                  {tUrl && s.tracking !== '—' ? (
                    <a href={tUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: mono, fontSize: 13, fontWeight: 700, color: T.accent, textDecoration: 'underline' }}>{s.tracking}</a>
                  ) : (
                    <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 700, color: T.muted }}>{s.tracking}</span>
                  )}
                  {s.courier && s.courier !== '—' && <SBadge label={s.courier} color={T.muted} bg={T.surfaceAlt} />}
                  {hasComments && <span style={{ width: 10, height: 10, borderRadius: 5, background: T.warn, display: 'inline-block' }} title="Has comments" />}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn variant="ghostSm" onClick={() => { setOpenComment(isCommentOpen ? null : s.id); setCommentText(''); }}>{IcMsg}</Btn>
                  <Btn variant="ghostSm" onClick={() => setEditShipId(isEditing ? null : s.id)}>{IcEdit} {isEditing ? Lk('save') : Lk('edit')}</Btn>
                  {isEditing && <Btn variant="dangerSm" onClick={() => doDeleteShipment(s.id)}>{IcTrash} {lang === 'zh' ? '删除' : 'Delete'}</Btn>}
                </div>
              </div>

              {/* Items with cost */}
              {(() => {
                let subtotal = 0;
                return (
                  <>
                    <div style={{ fontSize: 12, color: T.muted }}>
                      {items.map(item => {
                        const qtyKey = `${s.id}-${item.product_id}`;
                        const editVal = editItemQtys[qtyKey];
                        const price = item.unit_price || item.product?.quotes?.[supplier.id]?.price || item.product?.cost_price || 0;
                        const qty = item.ship_qty || 0;
                        const lineSub = price * qty;
                        subtotal += lineSub;
                        return (
                          <div key={item.product_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: `1px solid ${T.border}20` }}>
                            <span>• {item.product?.name}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontFamily: mono, fontSize: 11, color: T.muted }}>{money(price)}</span>
                              {isEditing ? (
                                <>
                                  <span style={{ color: T.dim }}>×</span>
                                  <input type="number" value={editVal !== undefined ? editVal : item.ship_qty}
                                    onChange={e => setEditItemQtys({ ...editItemQtys, [qtyKey]: e.target.value })}
                                    onBlur={() => { if (editVal !== undefined && parseInt(editVal) !== item.ship_qty) doEditItemQty(s.id, item.product_id, editVal); }}
                                    onKeyDown={e => { if (e.key === 'Enter') { doEditItemQty(s.id, item.product_id, editVal || item.ship_qty); e.target.blur(); } }}
                                    style={{ fontFamily: mono, fontSize: 11, padding: '3px 6px', width: 50, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 4, color: T.text, outline: 'none', textAlign: 'center' }} />
                                  <Btn variant="dangerSm" style={{ padding: '2px 8px', fontSize: 10 }} onClick={() => doRemoveItem(s.id, item.product_id)}>
                                    ✕ {lang === 'zh' ? '移除' : 'Remove'}
                                  </Btn>
                                </>
                              ) : (
                                <>
                                  <span style={{ fontFamily: mono, fontSize: 11, color: T.dim }}>× {qty}</span>
                                  <span style={{ fontFamily: mono, fontSize: 11, color: T.text, minWidth: 60, textAlign: 'right' }}>{money(lineSub)}</span>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Totals */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 6, padding: '6px 0', borderTop: `1px solid ${T.border}30` }}>
                      <span style={{ fontSize: 11, color: T.dim }}>Subtotal: <span style={{ fontFamily: mono, color: T.muted }}>{money(subtotal)}</span></span>
                      <span style={{ fontSize: 11, color: T.dim }}>{lang === 'zh' ? '运费' : 'Shipping'}:
                        {isEditing ? (
                          <input type="text" inputMode="decimal" value={editShipCost[s.id] !== undefined ? editShipCost[s.id] : (s.shipping_cost || '')}
                            onChange={e => setEditShipCost({ ...editShipCost, [s.id]: e.target.value })}
                            onBlur={() => { const v = editShipCost[s.id]; if (v !== undefined) doEditShipCost(s.id, v); }}
                            style={{ fontFamily: mono, fontSize: 11, padding: '2px 6px', width: 60, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 4, color: T.text, outline: 'none', marginLeft: 4 }} />
                        ) : (
                          <span style={{ fontFamily: mono, color: T.muted, marginLeft: 4 }}>{money((s.shipping_cost || 0))}</span>
                        )}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: T.accent }}>Total: <span style={{ fontFamily: mono }}>{money((subtotal + (s.shipping_cost || 0)))}</span></span>
                    </div>
                  </>
                );
              })()}

              {/* Comments */}
              {isCommentOpen && (
                <div style={{ marginTop: 10, padding: 12, background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase', marginBottom: 8 }}>{lang === 'zh' ? '物流备注' : 'Shipment Notes'}</div>
                  {comments.map(c => (
                    <div key={c.id} style={{ fontSize: 12, padding: '4px 0', borderBottom: `1px solid ${T.border}20` }}>
                      <span style={{ fontWeight: 700, color: c.author_type === 'admin' ? T.accent : T.success }}>{c.author}: </span>
                      <span style={{ color: T.text }}>{c.text}</span>
                      <span style={{ color: T.dim, fontSize: 10, marginLeft: 6 }}>{c.created_at?.split('T')[0]}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder={lang === 'zh' ? '添加备注...' : 'Add note...'}
                      onKeyDown={e => { if (e.key === 'Enter') doSendComment(s.id); }}
                      style={{ flex: 1, fontFamily: font, fontSize: 12, padding: '7px 10px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none' }} />
                    <Btn variant="small" style={{ padding: '5px 10px', fontSize: 11 }} onClick={() => doSendComment(s.id)}>{lang === 'zh' ? '发送' : 'Send'}</Btn>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </Sect>
    </div>
  );
}
